import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingBranch from '../../interfaces/IMappingBranch'
import { SearchRequest, MappingBranchSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingBranchList, mappingBranchSave } from '../../services/api'

interface MappingBranchContextState extends BaseContextState {
  mappingBranches: Array<IMappingBranch> | null,
  mappingBranch: IMappingBranch | null
}

interface MappingBranchContextData extends MappingBranchContextState {
  resetErrors: Function,
  requestMappingBranchList: (args: SearchRequest) => Promise<any>,
  requestMappingBranchFind: (args: SearchRequest) => Promise<any>,
  requestMappingBranchSave: (args: MappingBranchSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingBranchContextData)

// Export consumer
export const MappingBranchConsumer = Consumer

export class MappingBranchProvider extends BaseContextProvider {
  state: MappingBranchContextState = {
    mappingBranches: null,
    mappingBranch: null
  }

  /**
   * Make api request for mapping branch list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingBranchList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingBranchList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingBranches'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping branch finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingBranchFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingBranch: null
      }, async () => {
        payload.search = {
          ...payload.search,
          shelter: {
            with: {
              _fields: ["id", "uuid", "name"]
            }
          },
          branch: {
            with: true
          }
        }
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await mappingBranchList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingBranch'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping branch save
   * 
   * @param {MappingBranchSaveRequest} payload 
   */
  requestMappingBranchSave = (payload: MappingBranchSaveRequest) => {
    // No need to null `mappingBranch` on save: the form stays mounted (its render
    // no longer depends on this value) and re-fetches fresh whenever the modal reopens.
    return new Promise<void>((resolve, reject) => this.setState({
      fetching: true,
      error: null,
      // mappingBranch: null
    }, async () => {
      try {
        const response = await mappingBranchSave(payload)

        // Process our response
        const result = await this.processResponse(response, {
          mappings: [
            { origin: 'mapping_branch', destination: 'mappingBranch' }
          ]
        })

        // throw if error happend
        if (result.error) {
          return reject(new Error(result.error))
        }
      } finally {
        resolve()
      }
    }))
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingBranchList: this.requestMappingBranchList,
          requestMappingBranchFind: this.requestMappingBranchFind,
          requestMappingBranchSave: this.requestMappingBranchSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
